import type { MonsterDef } from '../../sim/registry';
import type { StatusApply } from '../../sim/skillTypes';
import { monsterStats } from '../balance';
import { circle, cone, rect, ring, teleport } from '../classes/kit';
import { areaStep, mAreas, mCharge, mRoar, mSkill, mSpin, mVolley } from './kit';

/**
 * Frostfang Keep (Act III dungeon, levels 27–29). Reuses `frost_ghoul` and
 * `ice_wraith` from Frosthollow; `hollow_knight` also marches in the Rift
 * Delve. The Frost Matriarch's script (frozen reflections, enrage) lives in
 * src/content/bosses/story.ts.
 */

const ICE = 0x9fe8ff;
const HOLLOW = 0x8a6aff;
const chill = (duration: number): StatusApply => ({ id: 'chill', duration });
const freeze = (duration: number): StatusApply => ({ id: 'freeze', duration });

/** Timing of the Hall of Mirrors mechanic (shared by the Matriarch and her reflections). */
export const MIRROR = { spawnAt: 0.5, eruptAt: 3.2, counter: [1.8, 1.4] as [number, number], duration: 4.8 };

/** Halberd elite of the Hollow Legion (Frostfang Keep, Rift Delve). */
export const HOLLOW_KNIGHT: MonsterDef = {
  id: 'hollow_knight', name: 'Hollow Knight', grade: 'elite', model: 'mob_hollow_knight', scale: 1, radius: 0.8, creature: 'Demon',
  stats: (l) => monsterStats(l, 'elite', { speed: 0.95 }),
  skills: [
    mAreas('hknight_sweep', 'Halberd Sweep', {
      steps: [{ shape: cone(4.6, 160), delay: 0.7, coef: 2, reaction: { kind: 'knockback', power: 2 } }],
      cd: 4, range: [0, 4.2], superArmor: 'paralysis', anim: 'Attack', hue: HOLLOW,
    }),
    // Counterable thrust: blue glow during the wind-up; a counter cancels the strike.
    mAreas('hknight_thrust', 'Hollow Thrust', {
      steps: [{ shape: rect(7, 1.8), delay: 1.2, coef: 3, reaction: { kind: 'knockdown', duration: 1.2, power: 2 }, cancelOnInterrupt: true }],
      counter: [0.45, 0.75], cd: 8, range: [0, 6.5], superArmor: 'paralysis', anim: 'Attack2', hue: HOLLOW,
    }),
    mSpin('hknight_whirl', 'Reaping Whirl', { radius: 3.6, count: 3, interval: 0.3, coef: 2.2, windup: 0.7, cd: 10, superArmor: 'paralysis', hue: HOLLOW }),
    mCharge('hknight_charge', 'Hollow Charge', { distance: 8, coef: 1.8, windup: 0.8, width: 1.8, cd: 9, range: [3, 8], superArmor: 'paralysis', hue: HOLLOW }),
  ],
  brain: 'melee', aggroRange: 11, leash: 26, xp: 260, loot: 'elite', weakness: 'holy',
};

export const KEEP_MONSTERS: MonsterDef[] = [
  HOLLOW_KNIGHT,
  {
    id: 'frost_matriarch', name: 'Frost Matriarch', grade: 'boss', model: 'boss_matriarch', scale: 1, radius: 1.1, creature: 'Spirit',
    title: 'Sovereign of Frostfang Keep', stagger: 800, hpBars: 48, weakness: 'fire',
    stats: (l) => monsterStats(l, 'boss', { hp: 1.1, speed: 0.8 }),
    skills: [
      mVolley('mat_lance_volley', 'Ice Lance Volley', {
        coef: 1.6, waves: 3, interval: 0.35, count: 5, spread: 60, speed: 20, reach: 22, windup: 0.7, status: [chill(2)],
        cd: 7, range: [0, 20], anim: 'Cast', element: 'water', hue: ICE,
      }),
      mVolley('mat_piercing_lance', 'Piercing Lance', {
        coef: 3.2, radius: 0.9, pierce: true, speed: 26, reach: 26, windup: 0.9, lane: 1.8, reaction: { kind: 'knockdown', duration: 1, power: 2 },
        cd: 9, range: [3, 25], anim: 'Cast', element: 'water', hue: ICE,
      }),
      mAreas('mat_frozen_spires', 'Frozen Spires', {
        steps: [0, 1, 2, 3, 4, 5].map((i) => ({ shape: circle(2, 2 + i * 2.5), delay: 0.8 + i * 0.15, coef: 2.2, reaction: { kind: 'launch' as const, power: 0.5, duration: 0.9 }, element: 'water' as const })),
        cd: 9, range: [0, 15], anim: 'Cast', hue: ICE,
      }),
      mAreas('mat_frost_nova', 'Frost Nova', {
        steps: [{ shape: circle(5), delay: 0.8, coef: 2.6, telegraph: 'blue', reaction: { kind: 'flinch' }, status: [freeze(1.5)], element: 'water' }],
        cd: 9, range: [0, 4.5], anim: 'Cast', hue: ICE,
      }),
      // Donut (freezing) then the inner circle.
      mAreas('mat_blizzard_ring', 'Blizzard Ring', {
        steps: [
          { shape: ring(4.5, 16), delay: 1.4, coef: 3.4, telegraph: 'blue', status: [freeze(1.5)], element: 'water' },
          { shape: circle(4.5), delay: 2.5, coef: 3.4, element: 'water' },
        ],
        cd: 16, range: [0, 12], anim: 'Cast', hue: ICE,
      }),
      // Blinks 7 m back, leaving a freezing mine where she stood.
      mSkill('mat_frost_step', 'Frost Step', {
        cd: 10, range: [0, 4], anim: 'Cast', duration: 1.2, hue: ICE,
        actions: [
          areaStep({ shape: circle(3.2), delay: 1, coef: 2.4, telegraph: 'blue', reaction: { kind: 'flinch' }, status: [freeze(1.5)], element: 'water' }),
          teleport(0.25, -7, false),
        ],
      }),
      // Counterable: a counter during the glow cancels the icicle.
      mAreas('mat_glacial_judgement', 'Glacial Judgement', {
        steps: [{ shape: circle(6.5, 3), delay: 1.5, coef: 4.5, telegraph: 'blue', reaction: { kind: 'knockdown', duration: 1.4, power: 2 }, status: [freeze(2)], cancelOnInterrupt: true, element: 'water' }],
        counter: [0.6, 0.9], cd: 14, range: [0, 7], anim: 'Cast', hue: ICE,
      }),
      // Hall of Mirrors (70% / 40%): the script moves her to one of three points and
      // spawns two reflections casting `refl_mirror`. Only she opens a counter window;
      // countering (or staggering) her shatters the reflections and cancels the requiem.
      mSkill('mat_mirror', 'Hall of Mirrors', {
        announce: 'Frozen reflections surround you! Only the true Matriarch can be countered!', announceStyle: 'warning',
        counter: MIRROR.counter, cd: 60, range: [0, 99], superArmor: 'full', anim: 'Channel', duration: MIRROR.duration, hue: ICE,
        actions: [
          areaStep({ at: MIRROR.spawnAt, shape: circle(40), anchor: 'point', delay: MIRROR.eruptAt - MIRROR.spawnAt, coef: 10, telegraph: 'blue', reaction: { kind: 'knockdown', duration: 1.5, power: 2 }, status: [freeze(2.5)], cancelOnInterrupt: true, element: 'water' }),
          areaStep({ at: MIRROR.spawnAt, shape: circle(6), delay: MIRROR.eruptAt - MIRROR.spawnAt, coef: 2, telegraph: 'blue', reaction: { kind: 'flinch' }, status: [freeze(2)], cancelOnInterrupt: true, element: 'water' }),
        ],
      }),
      mRoar('mat_fury', "Winter's Wrath", {
        announce: "The Frost Matriarch's wrath grips the keep!", announceStyle: 'warning', duration: 1.8, cd: 60, hue: ICE,
        nova: { shape: circle(7), delay: 0.9, coef: 1.5, telegraph: 'yellow', reaction: { kind: 'knockback', power: 4 } },
      }),
    ],
    brain: 'boss', aggroRange: 18, leash: 60, xp: 4000, loot: 'story_boss',
  },
  {
    // Helper spawned by the Matriarch's Hall of Mirrors; never placed by zones.
    id: 'frozen_reflection', name: 'Frozen Reflection', grade: 'elite', model: 'boss_matriarch', scale: 0.97, radius: 1.1, tint: 0xe6f6ff, creature: 'Illusion',
    stats: (l) => monsterStats(l, 'elite', { hp: 0.6, speed: 0 }),
    skills: [
      mSkill('refl_mirror', 'Mirrored Requiem', {
        cd: 60, range: [0, 99], superArmor: 'full', anim: 'Channel', duration: MIRROR.duration, hue: ICE,
        actions: [
          areaStep({ at: MIRROR.spawnAt, shape: circle(6), delay: MIRROR.eruptAt - MIRROR.spawnAt, coef: 2, telegraph: 'blue', reaction: { kind: 'flinch' }, status: [freeze(2)], cancelOnInterrupt: true, element: 'water' }),
        ],
      }),
    ],
    brain: 'boss', aggroRange: 0, leash: 99, xp: 0, loot: null, params: { immovable: true, helper: true },
  },
];
